import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { useDropzone } from "react-dropzone";
import { getAverageRGB } from "../utilities/Utilities";
import {
  plusIcon,
  starOutlineIcon,
  starSolidIcon,
  closeModalIcon,
} from "./Icons";
import merge from "lodash/merge";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#FAFB00",
  borderStyle: "dashed",
  color: "#FAFB00",
  outline: "none",
  transition: "border .24s ease-in-out, color .34s ease-in-out",
  margin: "0 20px",
};

const activeStyle = {
  borderColor: "#4400FF",
  color: "#4400FF",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

function ImageDialog(props) {
  const [imgData, setImgData] = useState(null);
  const [colorAttribute, setColorAttribute] = useState([]);
  const [colorAttributeFound, setColorAttributeFound] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [colorDelta, setColorDelta] = useState(5);
  const [imageMessage, setImageMessage] = useState(null);
  const [accuracy, setAccuracy] = useState(null);

  const { openImageDialog, setOpenImageDialog, csi, handleStore, store } =
    props;
  const MIN_WIDTH = 400;
  const handleClose = () => {
    setOpenImageDialog(false);
  };
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    acceptedFiles,
  } = useDropzone({
    getFilesFromEvent: async (event) => {
      const files = event.target.files || event.dataTransfer.files;
      const promises = [];
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        const promise = new Promise((resolve, reject) => {
          const image = new Image();
          let url = "";
          image.onload = function () {
            file.width = image.width;
            file.height = image.height;
            resolve(file);
          };
          url = URL.createObjectURL(file);
          image.src = url;
        });
        promises.push(promise);
      }
      return await Promise.all(promises);
    },
    validator: (file) => {
      // You can access width/height properties
      if (file.width < MIN_WIDTH) {
        setImageMessage(`Image width must be greater than ${MIN_WIDTH}px`);
        return {
          code: "small-width",
          message: `Image width must be greater than ${MIN_WIDTH}`,
        };
      }
      setImageMessage(null);
      return null;
    },
    maxFiles: 1,
    accept: "image/jpeg, image/png, image/jpg",
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  useEffect(() => {
    if (acceptedFiles[0]) {
      setColorDelta(5);
      setFiltered([]);
      //console.log('accetpted file changed')
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImgData(reader.result);
      });
      reader.readAsDataURL(acceptedFiles[0]);
    }
  }, [acceptedFiles]);

  function getDiff(a, x) {
    if (x.length === 3) {
      const getSum = x
        .map((item, i) => Math.abs(item - a.colorAtt[i]))
        .reduce((a, b) => a + b, 0);
      return getSum;
    }
    return 0;
  }

  useEffect(() => {
    if (colorAttributeFound.length >= 1) {
      const filterRange = colorAttributeFound.map((item) =>
        getDiff(item[0], colorAttribute)
      );
      const csiScore = colorAttributeFound.map((item, i) =>
        merge(
          [
            {
              hex: item[0].hex,
              csi_code: item[0].csi_code,
              csi_name: item[0].csi_name,
              isDark: item[0].isDark,
            },
          ],
          [{ score: filterRange[i] }]
        )
      );
      setFiltered(csiScore);
    }
    //return () => setFiltered([]);
  }, [colorAttributeFound, colorAttribute]);

  useEffect(() => {
    if (imgData) {
      const colorRGB = getAverageRGB(document.getElementById("i"));
      setColorAttribute(colorRGB);
    }
  }, [imgData]);

  useEffect(() => {
    if (imgData) {
      const result = csi.filter(
        (item) =>
          colorAttribute[0] >= item[0].colorAtt[0] - colorDelta &&
          colorAttribute[0] <= item[0].colorAtt[0] + colorDelta &&
          colorAttribute[1] >= item[0].colorAtt[1] - colorDelta &&
          colorAttribute[1] <= item[0].colorAtt[1] + colorDelta &&
          colorAttribute[2] >= item[0].colorAtt[2] - colorDelta &&
          colorAttribute[2] <= item[0].colorAtt[2] + colorDelta
      );

      if (result.length <= 5) {
        setColorDelta(colorDelta + 1);
      } else {
        setAccuracy(getAccuracy(colorDelta));
        setColorAttributeFound(result);
      }
    }

    // eslint-disable-next-line
  }, [colorAttribute, colorDelta, imgData]);

  const handleClearImage = () => {
    setImgData(null);
    setFiltered([]);
    setColorDelta(5);
  };

  function getAccuracy(accuracy) {
    if (accuracy > 0 && accuracy <= 15) return "good";
    if (accuracy >= 16 && accuracy <= 25) return "average";
    if (accuracy >= 26 && accuracy <= 35) return "pretty good";
    if (accuracy >= 36 && accuracy <= 99) return "not great";
    if (accuracy > 100) return;
  }

  function getGrad() {
    if (filtered.length >= 2) {
      const gradFirst = filtered.find((item) => item[0]);
      return gradFirst[0].hex;
    }
    return "#4400FF";
  }
  if (openImageDialog) {
    return (
      <Dialog
        maxWidth={"md"}
        open={openImageDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        transitionDuration={{ enter: 10 }}
      >
        <div className="image-dialog">
          <div className="close-modal-icon">
            <span onClick={handleClose}>{closeModalIcon}</span>
          </div>
          {imgData ? (
            <div
              className="image-container"
              style={{ backgroundImage: `url(${imgData})` }}
            >
               <div className="close-modal-icon" style={{left:'10px'}}>
            <span onClick={handleClose}>{closeModalIcon}</span>
          </div>
              <img src={imgData} id="i" alt="SourceRGB" />
              <div className="drop-zone-small" {...getRootProps({ style })}>
                <input {...getInputProps()} />
                {plusIcon}
                <h3>Drop New Image</h3>
              </div>
              <div className="clear-image" onClick={handleClearImage}>
                <h5>Clear Image</h5>
              </div>
            </div>
          ) : (
            <div className="drop-container">
              <div className="drop-text">
                <h3>Select an image and find closest CSI color match</h3>
                <h5>
                  Accuracy is based on an algorithm that looks for nearest match
                  to the image color. You can adjust the delta variable for more
                  results.
                </h5>
              </div>
              <div className="drop-zone" {...getRootProps({ style })}>
                <input {...getInputProps()} />
                {plusIcon}
                <h3>Drag image here or click to select image</h3>
                {imageMessage ? <h4>{imageMessage}</h4> : null}
              </div>
            </div>
          )}

          {imgData && (
            <div className="image-results-tray">
              <div className="image-results-tray-header">
              {imageMessage ? <h3 style={{color:'#FAFB00'}}>An Error Occurred with this image</h3> :<h3>{filtered.length} Filtered results</h3>}
                {imageMessage ? (
                  <h6>{imageMessage}</h6>
                ) : (
                  <h6>{`Color Accuracy for this image is: ${accuracy}`}</h6>
                )}
              </div>
              <div
                className="image-filtered-results"
                style={{
                  background: `linear-gradient(165deg, rgba(31,41,55,1) 0%, rgba(31,41,55,1) 71%, ${
                    getGrad() + 40
                  } 100%)`,
                }}
              >
                {filtered &&
                  filtered
                    .sort((a, b) => a[0].score - b[0].score)
                    .map((item) => (
                      <div
                        className="image-filtered-swatch"
                        key={item[0].csi_code}
                        data-score={item[0].score}
                        style={{
                          background: item[0].hex,
                          color: item[0].isDark ? "#FEFEFE" : "#000",
                        }}
                      >
                        <span
                          className="similar-thumb-triangle"
                          style={{
                            background: item[0].isDark
                              ? "rgba(0,0,0,0.1)"
                              : "rgba(0,0,0,0.05)",
                          }}
                        ></span>
                        {store.includes(item[0].csi_code) ? (
                          <div
                            className="store-button image-filtered"
                            style={{
                              color: item[0].isDark ? "white" : "black",
                            }}
                            onClick={() => handleStore(item[0].csi_code)}
                          >
                            {starSolidIcon}
                          </div>
                        ) : (
                          <div
                            className="store-button image-filtered"
                            style={{
                              color: item[0].isDark ? "white" : "black",
                            }}
                            onClick={() => handleStore(item[0].csi_code)}
                          >
                            {starOutlineIcon}
                          </div>
                        )}
                        {item[0].csi_code} - {item[0].csi_name}{" "}
                      </div>
                    ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>
    );
  }
  return null;
}

export default ImageDialog;
